import React, { useState, useEffect } from 'react';
import { consumosAPI, clientsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  BarChart3,
  Users,
  DollarSign,
  FileText,
  Filter,
  Calendar,
  Search,
  X,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Loader2,
  TrendingUp,
  Building2
} from 'lucide-react';

const UserConsumption = () => {
  const [consumos, setConsumos] = useState([]);
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    clientId: '',
    fechaDesde: '',
    fechaHasta: '',
    tipoComprobante: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { isSuperAdmin, isAdmin } = useAuth();

  useEffect(() => {
    if (isSuperAdmin() || isAdmin()) {
      loadInitialData();
    }
  }, [isSuperAdmin, isAdmin]);

  useEffect(() => {
    if (isSuperAdmin() || isAdmin()) {
      loadConsumos();
    }
  }, [filters, currentPage]);

  const loadInitialData = async () => {
    try {
      const [clientsRes, statsRes] = await Promise.all([
        clientsAPI.getAll(),
        consumosAPI.getStats()
      ]);
      
      setClients(clientsRes.data.items || clientsRes.data || []);
      setStats(statsRes.data || {});
    } catch (error) {
      console.error('Error cargando datos iniciales:', error);
    }
  };

  const loadConsumos = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        page: currentPage,
        pageSize: 20
      };

      // Remover parámetros vacíos
      Object.keys(params).forEach(key => {
        if (params[key] === '') {
          delete params[key];
        }
      });

      const response = await consumosAPI.getAll(params);
      setConsumos(response.data.items || response.data || []);
      setTotalPages(Math.ceil((response.data.totalCount || 0) / 20));
    } catch (error) {
      console.error('Error cargando consumos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      clientId: '',
      fechaDesde: '',
      fechaHasta: '',
      tipoComprobante: ''
    });
    setCurrentPage(1);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTipoComprobanteText = (tipo) => {
    const tipos = {
      'I': 'Ingreso',
      'E': 'Egreso',
      'T': 'Traslado',
      'N': 'Nómina',
      'P': 'Pago'
    };
    return tipos[tipo] || tipo;
  };

  const getTipoComprobanteBadge = (tipo) => {
    const config = {
      'I': { class: 'status-active', icon: TrendingUp },
      'E': { class: 'status-inactive', icon: TrendingUp },
      'T': { class: 'status-pending', icon: RefreshCw },
      'N': { class: 'status-processing', icon: Users },
      'P': { class: 'status-active', icon: DollarSign }
    };
    
    const typeConfig = config[tipo] || { class: 'status-pending', icon: FileText };
    const IconComponent = typeConfig.icon;
    
    return (
      <span className={`${typeConfig.class} flex items-center space-x-1`}>
        <IconComponent className="w-3 h-3" />
        <span>{getTipoComprobanteText(tipo)}</span>
      </span>
    );
  };

  const hasActiveFilters = filters.clientId || filters.fechaDesde || filters.fechaHasta || filters.tipoComprobante;

  if (!isSuperAdmin() && !isAdmin()) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="alert alert-error">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <span>No tienes permisos para acceder a esta sección.</span>
          </div>
        </div>
      </div>
    );
  }

  if (loading && currentPage === 1) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Cargando consumos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header principal con gradiente */}
        <div className="mb-8">
          <div className="card p-8 bg-gradient-to-r from-primary-500 via-primary-600 to-secondary-600 text-white relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full transform translate-x-32 -translate-y-32"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-accent-400 rounded-full transform -translate-x-24 translate-y-24"></div>
            </div>

            <div className="relative z-10">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl lg:text-4xl font-bold">Consumo de Usuarios</h1>
                  <p className="text-white/80 text-lg">Facturas procesadas y estadísticas de consumo</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Estadísticas generales */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card p-6 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-600 dark:text-blue-400 text-sm font-medium mb-1">Total Facturas</p>
                <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                  {stats.totalInvoices?.toLocaleString() || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="card p-6 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-600 dark:text-green-400 text-sm font-medium mb-1">Monto Total</p>
                <p className="text-2xl font-bold text-green-700 dark:text-green-300">
                  {formatCurrency(stats.totalAmount || 0)}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="card p-6 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-600 dark:text-purple-400 text-sm font-medium mb-1">Clientes Activos</p>
                <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">
                  {stats.totalClients || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Actions Bar */}
        <div className="mb-6">
          <div className="card p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div className="flex items-center space-x-3">
                <Filter className="w-5 h-5 text-gray-500" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Filtros y Búsqueda</h3>
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`btn ${showFilters ? 'btn-primary' : 'btn-ghost'}`}
                >
                  <Filter className="w-4 h-4 mr-2" />
                  {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
                </button>
                <button
                  onClick={() => loadConsumos()}
                  className="btn-primary"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Actualizar
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Panel de Filtros */}
        {showFilters && (
          <div className="card mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-blue-200 dark:border-blue-700">
            <div className="flex items-center space-x-2 mb-6">
              <Search className="w-5 h-5 text-primary-500" />
              <h3 className="text-lg font-bold text-primary-700 dark:text-primary-300">
                Filtros de Búsqueda
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Cliente
                </label>
                <select
                  name="clientId"
                  className="input-field"
                  value={filters.clientId}
                  onChange={handleFilterChange}
                >
                  <option value="">Todos los clientes</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.rfc} - {client.razonSocial}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Fecha Desde
                </label>
                <input
                  type="date"
                  name="fechaDesde"
                  className="input-field"
                  value={filters.fechaDesde}
                  onChange={handleFilterChange}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Fecha Hasta
                </label>
                <input
                  type="date"
                  name="fechaHasta"
                  className="input-field"
                  value={filters.fechaHasta}
                  onChange={handleFilterChange}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tipo Comprobante
                </label>
                <select
                  name="tipoComprobante"
                  className="input-field"
                  value={filters.tipoComprobante}
                  onChange={handleFilterChange}
                >
                  <option value="">Todos</option>
                  <option value="I">Ingreso</option>
                  <option value="E">Egreso</option>
                  <option value="T">Traslado</option>
                  <option value="N">Nómina</option>
                  <option value="P">Pago</option>
                </select>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={clearFilters}
                disabled={!hasActiveFilters}
                className="btn-ghost"
              >
                <X className="w-4 h-4 mr-2" />
                Limpiar Filtros
              </button>
              <button
                onClick={() => loadConsumos()}
                className="btn-primary"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Aplicar Filtros
              </button>
            </div>
          </div>
        )}

        {/* Tabla de facturas */}
        <div className="card">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
            <div className="flex items-center space-x-3 mb-4 lg:mb-0">
              <FileText className="w-5 h-5 text-primary-500" />
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Facturas Procesadas</h3>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Página {currentPage} de {totalPages}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">Cargando facturas...</p>
              </div>
            </div>
          ) : consumos.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No se encontraron facturas
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                No hay facturas que coincidan con los filtros aplicados.
              </p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="btn-primary"
                >
                  <X className="w-4 h-4 mr-2" />
                  Limpiar Filtros
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th className="text-left">UUID</th>
                      <th className="text-left">Cliente</th>
                      <th className="text-left">Fecha Factura</th>
                      <th className="text-left">Tipo</th>
                      <th className="text-right">Subtotal</th>
                      <th className="text-right">IVA</th>
                      <th className="text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consumos.map((consumo) => (
                      <tr key={consumo.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                        <td>
                          <div className="font-mono text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                            {consumo.uuid?.substring(0, 8)}...
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center space-x-2">
                            <Building2 className="w-4 h-4 text-gray-400" />
                            <span className="font-medium">{consumo.clienteRfc}</span>
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center space-x-2">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span>{formatDate(consumo.fechaFactura)}</span>
                          </div>
                        </td>
                        <td>
                          {getTipoComprobanteBadge(consumo.tipoComprobante)}
                        </td>
                        <td className="text-right font-medium">
                          {formatCurrency(consumo.subtotal)}
                        </td>
                        <td className="text-right font-medium">
                          {formatCurrency(consumo.iva)}
                        </td>
                        <td className="text-right font-bold text-lg">
                          {formatCurrency(consumo.total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Paginación */}
              <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Mostrando {((currentPage - 1) * 20) + 1} - {Math.min(currentPage * 20, stats.totalInvoices || 0)} de {stats.totalInvoices || 0} facturas
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="btn-ghost flex items-center space-x-1"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    <span>Anterior</span>
                  </button>
                  
                  <div className="flex items-center space-x-1">
                    <span className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                      {currentPage} / {totalPages}
                    </span>
                  </div>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="btn-ghost flex items-center space-x-1"
                  >
                    <span>Siguiente</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserConsumption;